import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { usePartyFilter } from '../context/PartyFilterContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatEuro, formatPercent, formatNumber } from '../utils/formatters';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Calendar, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CashflowPage = () => {
  const { t } = useLanguage();
  const { selectedPartyId } = usePartyFilter();
  const [cashflow, setCashflow] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // CAPEX override state
  const [capexOverrides, setCapexOverrides] = useState({});
  const [editingCapex, setEditingCapex] = useState(null);
  const [capexInput, setCapexInput] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ years: '6' });
      if (selectedPartyId !== 'all') {
        params.append('party_id', selectedPartyId);
      }
      
      const cashflowRes = await axios.get(`${API}/dashboard/cashflow?${params}`, { withCredentials: true });
      
      // Apply local CAPEX overrides to cashflow data
      const cashflowData = cashflowRes.data.map(row => {
        const yearStr = String(row.year);
        if (capexOverrides[yearStr] !== undefined) {
          const overriddenCapex = parseFloat(capexOverrides[yearStr]);
          const nri = row.gri - overriddenCapex;
          const surplus = nri - row.interest - row.repayment;
          const icr = row.interest > 0 ? nri / row.interest : 0;
          const dscr = (row.interest + row.repayment) > 0 ? nri / (row.interest + row.repayment) : 0;
          const debtYield = row.outstanding > 0 ? (nri / row.outstanding * 100) : 0;
          
          return {
            ...row,
            capex: overriddenCapex,
            capex_overridden: true,
            nri: nri,
            surplus: surplus,
            icr: icr,
            dscr: dscr,
            debt_yield: debtYield
          };
        }
        return { ...row, capex_overridden: false };
      });
      
      setCashflow(cashflowData);
    } catch (error) {
      console.error('Error fetching cashflow:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [selectedPartyId, capexOverrides, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditCapex = (year, currentValue) => {
    setEditingCapex(year);
    setCapexInput(String(Math.round(currentValue)));
  };

  const handleSaveCapex = (year) => {
    const value = parseFloat(capexInput);
    if (isNaN(value) || value < 0) {
      toast.error('Please enter a valid CAPEX amount');
      return;
    }
    
    setCapexOverrides(prev => ({
      ...prev,
      [String(year)]: value
    }));
    setEditingCapex(null);
    setCapexInput('');
    toast.success(`CAPEX for ${year} updated to €${value.toLocaleString()}`);
  };

  const handleCancelEdit = () => {
    setEditingCapex(null);
    setCapexInput('');
  };

  const handleResetCapex = (year) => {
    setCapexOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[String(year)];
      return newOverrides;
    });
    toast.success(`CAPEX for ${year} reset to default`);
  };

  const handleResetAllCapex = () => {
    setCapexOverrides({});
    toast.success('All CAPEX overrides reset');
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-[500px] bg-slate-800" />
        </div>
      </Layout>
    );
  }

  const hasOverrides = Object.keys(capexOverrides).length > 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="cashflow-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Comfortaa'] flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-[#b4976f]" />
              {t('cashflowOverview')}
            </h1>
            <p className="text-slate-400 mt-1">
              Multi-year financial projection based on current portfolio
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Reset All Overrides Button */}
            {hasOverrides && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResetAllCapex}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <X className="h-4 w-4 mr-1" />
                Reset CAPEX
              </Button>
            )}
          </div>
        </div>

        {/* Cashflow Table */}
        <Card className="bg-[#1e293b] border-slate-700 overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400 sticky left-0 bg-[#1e293b] z-10 min-w-[180px]">
                    {t('year')}
                  </TableHead>
                  {cashflow.map((row) => (
                    <TableHead 
                      key={row.year} 
                      className={`text-center min-w-[120px] ${row.year_index === 0 ? 'bg-[#264162]/50' : ''}`}
                    >
                      <div className="flex items-center justify-center gap-1 text-white">
                        <Calendar className="h-4 w-4 text-[#b4976f]" />
                        {row.year}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* SQM Total */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    SQM Total
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-white ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatNumber(row.sqm_total)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Occupancy */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('occupancy')} %
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center ${row.occupancy < 90 ? 'text-amber-400' : 'text-white'} ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatPercent(row.occupancy, 1)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Divider */}
                <TableRow className="border-slate-600 bg-slate-800/30">
                  <TableCell colSpan={cashflow.length + 1} className="py-1"></TableCell>
                </TableRow>

                {/* GRI - Gross Rental Income */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('gri')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-emerald-400 euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatEuro(row.gri)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* CAPEX - Editable */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    <div className="flex items-center gap-2">
                      {t('capex')}
                      <span className="text-xs text-slate-500">(click to edit)</span>
                    </div>
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell 
                      key={row.year} 
                      className={`text-center ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}
                    >
                      {editingCapex === row.year ? (
                        <div className="flex items-center gap-1 justify-center">
                          <Input
                            type="number"
                            value={capexInput}
                            onChange={(e) => setCapexInput(e.target.value)}
                            className="w-24 h-7 text-xs bg-slate-900 border-slate-600 text-white text-center"
                            autoFocus
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSaveCapex(row.year);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleSaveCapex(row.year)}>
                            <Check className="h-3 w-3 text-emerald-400" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleCancelEdit}>
                            <X className="h-3 w-3 text-red-400" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className={`group flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-800/50 rounded px-1 py-0.5 ${row.capex_overridden ? 'text-amber-400' : 'text-amber-400'}`}
                          onClick={() => handleEditCapex(row.year, row.capex)}
                        >
                          <span className="euro-amount">{formatEuro(row.capex)}</span>
                          <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-100 text-slate-500" />
                          {row.capex_overridden && (
                            <button 
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 ml-1"
                              onClick={(e) => { e.stopPropagation(); handleResetCapex(row.year); }}
                              title="Reset to default"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* NRI - Net Rental Income */}
                <TableRow className="border-slate-700 bg-slate-800/30">
                  <TableCell className="text-white font-semibold sticky left-0 bg-slate-800/30 z-10">
                    {t('nri')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-white font-semibold euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''} ${row.capex_overridden ? 'text-amber-300' : ''}`}>
                      {formatEuro(row.nri)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Divider */}
                <TableRow className="border-slate-600 bg-slate-800/30">
                  <TableCell colSpan={cashflow.length + 1} className="py-1"></TableCell>
                </TableRow>

                {/* Interest */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('interestAmount')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-red-400 euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatEuro(row.interest)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Repayment */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('repaymentAmount')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-amber-400 euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatEuro(row.repayment)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Surplus */}
                <TableRow className="border-slate-700 bg-slate-800/50">
                  <TableCell className="text-white font-semibold sticky left-0 bg-slate-800/50 z-10">
                    {t('surplus')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell 
                      key={row.year} 
                      className={`text-center font-semibold euro-amount ${
                        row.surplus >= 0 ? 'text-emerald-400' : 'text-red-400'
                      } ${row.year_index === 0 ? 'bg-[#264162]/30' : ''} ${row.capex_overridden ? 'ring-1 ring-amber-500/30' : ''}`}
                    >
                      {formatEuro(row.surplus)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Divider */}
                <TableRow className="border-slate-600 bg-slate-800/30">
                  <TableCell colSpan={cashflow.length + 1} className="py-1"></TableCell>
                </TableRow>

                {/* Outstanding */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('outstanding')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-white euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatEuro(row.outstanding)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Market Value */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('marketValue')} (MV)
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-[#c8b599] euro-amount ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatEuro(row.market_value)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* LTV */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('loanToValue')} (LTV)
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-white ${row.year_index === 0 ? 'bg-[#264162]/30' : ''}`}>
                      {formatPercent(row.ltv)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Divider */}
                <TableRow className="border-slate-600 bg-slate-800/30">
                  <TableCell colSpan={cashflow.length + 1} className="py-1"></TableCell>
                </TableRow>

                {/* Debt Yield */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    {t('debtYield')}
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center text-white ${row.year_index === 0 ? 'bg-[#264162]/30' : ''} ${row.capex_overridden ? 'text-amber-300' : ''}`}>
                      {formatPercent(row.debt_yield)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* ICR */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    ICR
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center ${row.icr < 1.2 ? 'text-red-400' : row.icr < 1.5 ? 'text-amber-400' : 'text-white'} ${row.year_index === 0 ? 'bg-[#264162]/30' : ''} ${row.capex_overridden ? 'font-medium' : ''}`}>
                      {formatNumber(row.icr, 2)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* DSCR */}
                <TableRow className="border-slate-700">
                  <TableCell className="text-slate-300 font-medium sticky left-0 bg-[#1e293b] z-10">
                    DSCR
                  </TableCell>
                  {cashflow.map((row) => (
                    <TableCell key={row.year} className={`text-center ${row.dscr < 1.1 ? 'text-red-400' : row.dscr < 1.25 ? 'text-amber-400' : 'text-white'} ${row.year_index === 0 ? 'bg-[#264162]/30' : ''} ${row.capex_overridden ? 'font-medium' : ''}`}>
                      {formatNumber(row.dscr, 2)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info Note */}
        <Card className="bg-[#264162]/30 border-[#b4976f]/30">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">
              <span className="text-[#b4976f] font-semibold">Note:</span> Projections assume 2% annual GRI growth. 
              CAPEX is calculated based on the property-level CAPEX % setting.
              <span className="text-amber-400 font-medium"> Click on any CAPEX value to override it for scenario analysis.</span>
              {' '}Interest and repayment amounts remain constant based on current loan terms. 
              ICR below 1.2x and DSCR below 1.1x are highlighted as risk indicators.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
